// v2/components/FilterStrips.js — kayar arac markasi ve kategori seritleri
import React, { useRef, useState, useCallback, useEffect } from 'react';
import { ChevronLeft, ChevronRight, X, Cog, Zap, Disc3, Waves, Filter, Package } from 'lucide-react';
import { CATEGORIES } from '../constants/catalog';
import { VEHICLE_BRANDS, CATEGORY_VISUALS } from '../constants/vehicles';
import styles from './FilterStrips.module.css';

const ICONS = { Cog, Zap, Disc3, Waves, Filter, Package };

/** Yatay kaydirilabilir serit: oklar, tekerlek, klavye */
export function Strip({ label, children, count }) {
  const ref = useRef(null);
  const [edge, setEdge] = useState({ left: false, right: false });

  const measure = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    setEdge({
      left: el.scrollLeft > 4,
      right: el.scrollLeft + el.clientWidth < el.scrollWidth - 4,
    });
  }, []);

  useEffect(() => {
    measure();
    const el = ref.current;
    if (!el) return undefined;
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [measure, count]);

  const scrollBy = (dir) => {
    const el = ref.current;
    if (!el) return;
    el.scrollBy({ left: dir * Math.max(el.clientWidth * 0.7, 200), behavior: 'smooth' });
  };

  // fare tekerlegi -> yatay kaydirma
  const onWheel = (e) => {
    const el = ref.current;
    if (!el || e.deltaY === 0 || Math.abs(e.deltaX) > Math.abs(e.deltaY)) return;
    el.scrollLeft += e.deltaY;
  };

  return (
    <div className={styles.strip}>
      <span className={styles.stripLabel}>{label}</span>

      <div className={styles.stripArea}>
        <button
          type="button"
          className={`${styles.navBtn} ${styles.navLeft} ${edge.left ? styles.navOn : ''}`}
          onClick={() => scrollBy(-1)} tabIndex={edge.left ? 0 : -1}
          aria-label="Geri kaydır"
        >
          <ChevronLeft size={16} />
        </button>

        <div
          ref={ref} className={styles.track}
          onScroll={measure} onWheel={onWheel}
          role="group" aria-label={label}
        >
          {children}
        </div>

        <button
          type="button"
          className={`${styles.navBtn} ${styles.navRight} ${edge.right ? styles.navOn : ''}`}
          onClick={() => scrollBy(1)} tabIndex={edge.right ? 0 : -1}
          aria-label="İleri kaydır"
        >
          <ChevronRight size={16} />
        </button>

        <span className={`${styles.fade} ${styles.fadeLeft} ${edge.left ? styles.fadeOn : ''}`} />
        <span className={`${styles.fade} ${styles.fadeRight} ${edge.right ? styles.fadeOn : ''}`} />
      </div>
    </div>
  );
}

export default function FilterStrips({
  brand = null, onBrandChange = () => {},
  category = null, onCategoryChange = () => {},
}) {
  const pick = (cur, next, fn) => fn(cur === next ? null : next);

  const activeBrand = VEHICLE_BRANDS.find((b) => b.id === brand);

  return (
    <section className={styles.wrap} aria-label="Hızlı filtreler">
      <Strip label="Araç Markası" count={VEHICLE_BRANDS.length}>
        {VEHICLE_BRANDS.map((b, i) => {
          const on = b.id === brand;
          return (
            <button
              key={b.id} type="button"
              className={`${styles.brandCard} ${on ? styles.cardOn : ''}`}
              style={{ animationDelay: `${Math.min(i * 22, 260)}ms` }}
              onClick={() => pick(brand, b.id, onBrandChange)}
              aria-pressed={on}
            >
              <span className={styles.brandMark} style={{ background: b.color }}>
                {b.logo
                  ? <img className={styles.brandImg} src={b.logo} alt="" />
                  : b.short}
              </span>
              <span className={styles.brandName}>{b.name}</span>
            </button>
          );
        })}
      </Strip>

      <Strip label="Kategori" count={CATEGORIES.length}>
        {CATEGORIES.map((c, i) => {
          const v = CATEGORY_VISUALS[c] || {};
          const Icon = ICONS[v.icon] || Package;
          const on = c === category;
          return (
            <button
              key={c} type="button"
              className={`${styles.catCard} ${on ? styles.cardOn : ''}`}
              style={{ animationDelay: `${Math.min(i * 30, 260)}ms` }}
              onClick={() => pick(category, c, onCategoryChange)}
              aria-pressed={on}
            >
              <span className={styles.catIcon} style={{ color: v.tint }}>
                {v.image
                  ? <img className={styles.catImg} src={v.image} alt="" />
                  : <Icon size={20} strokeWidth={1.7} />}
              </span>
              <span className={styles.catName}>{c}</span>
            </button>
          );
        })}
      </Strip>

      {(activeBrand || category) && (
        <div className={styles.chosen}>
          <span className={styles.chosenLabel}>Seçili:</span>
          {activeBrand && (
            <button type="button" className={styles.chosenChip}
                    onClick={() => onBrandChange(null)}>
              {activeBrand.name} <X size={12} />
            </button>
          )}
          {category && (
            <button type="button" className={styles.chosenChip}
                    onClick={() => onCategoryChange(null)}>
              {category} <X size={12} />
            </button>
          )}
          <button type="button" className={styles.chosenClear}
                  onClick={() => { onBrandChange(null); onCategoryChange(null); }}>
            Tümünü kaldır
          </button>
        </div>
      )}
    </section>
  );
}
